/* analytics */
import { useEffect } from 'react'
import styled from 'styled-components'
import 'core-js/stable'
import ahoy from 'ahoy.js'

import useReduxSelector from 'hooks/useReduxSelector'
import useHideIntercom from 'hooks/useHideIntercom'
import { dispatchRollbarError } from 'utils/rollbar'
import { useUtmParams } from 'utils/utmParams'

import Container from '../../../components/layout/Container'

import Description from './Description'
import Header from './Header'
import Items from './Items'
import OtherCollections from './OtherCollections'

const OtherCollectionsWrapper = styled.section`
  margin: 40px 0;
`

const useAnalyticsPageViewed = ({
  id,
  name,
  category,
  utmParams = {},
}: {
  id: string | number
  name: string
  category: string
  utmParams?: Record<string, string>
}) => {
  useEffect(() => {
    analytics.track('Collection Viewed', {
      collection_id: id,
      collection_name: name,
      collection_category: category,
      ...utmParams,
    })
    ahoy.track('Viewed collection', {
      collection_id: id,
    })
  }, [])
}

const usePrerenderedCollectionData = () => {
  const reduxData = useReduxSelector((state) => state?.collection)

  const { data, fetched } = reduxData

  const noData = fetched && !data

  useEffect(() => {
    if (noData) {
      dispatchRollbarError('Collection page prerender data missing', {
        description:
          'The collection page relies on the rails route, controller, and view to provide prerender data, which is then stored in redux. This data was not there this time.',
        collectionReduxState: reduxData,
      })
    }
  }, [noData])

  return reduxData
}

const Collection = () => {
  const { data: prehydratedCollection } = usePrerenderedCollectionData()

  const collectionId = prehydratedCollection?.id?.toString()
  const marketId = prehydratedCollection?.markets?.at(0)?.id?.toString()

  const { id, name, image, category } = prehydratedCollection

  const utmParams = useUtmParams()

  useAnalyticsPageViewed({ id, name, category, utmParams })
  useHideIntercom()

  return (
    <>
      <Header image={image} name={name} />
      <Container>
        <Description text={prehydratedCollection?.description} />
      </Container>
      <div>
        <Items collectionId={collectionId} />
      </div>

      <OtherCollectionsWrapper>
        <OtherCollections collectionId={collectionId} marketId={marketId} />
      </OtherCollectionsWrapper>
    </>
  )
}

export default Collection
